import { Link } from 'react-router-dom';
import type { AuditFinding } from './types';
import {
  FINDING_STATUS_LABEL,
  FINDING_TYPE_LABEL,
  findingTypeBadgeClass,
} from './auditUtils';
import { DateCell } from '../../components/DateCell';

interface AuditFindingsTabProps {
  findings: AuditFinding[];
  canEdit: boolean;
  onEscalate: (finding: AuditFinding) => void;
  onPromoteToRisk?: (finding: AuditFinding) => void;
  escalatingId?: string | null;
  promotingId?: string | null;
}

export function AuditFindingsTab({
  findings,
  canEdit,
  onEscalate,
  onPromoteToRisk,
  escalatingId,
  promotingId,
}: AuditFindingsTabProps) {
  if (findings.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="audit-findings-empty">
        No hay hallazgos registrados.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="audit-findings-tab">
      {findings.map((finding) => {
        const isNc = finding.type === 'no_conformidad';
        const ncDisabled = Boolean(finding.qmsRecordId) || !canEdit || !isNc;
        const promoteDisabled =
          Boolean(finding.promotedRiskId) || !canEdit || !onPromoteToRisk;

        return (
          <div
            key={finding.id}
            className="border border-border rounded-lg p-4 space-y-2"
            data-testid={`finding-row-${finding.id}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded ${findingTypeBadgeClass(finding.type)}`}
              >
                {FINDING_TYPE_LABEL[finding.type]}
              </span>
              {finding.severity && (
                <span className="text-xs text-muted-foreground">
                  Severidad: {finding.severity}
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                {FINDING_STATUS_LABEL[finding.status]}
              </span>
            </div>
            <p className="text-sm">{finding.description}</p>
            {finding.clause && (
              <p className="text-xs text-muted-foreground">Cláusula: {finding.clause}</p>
            )}
            {finding.dueDate && (
              <p className="text-xs text-muted-foreground">
                Vence: <DateCell value={finding.dueDate} />
              </p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              {finding.promotedRiskId && (
                <Link
                  to={`/riesgos/${finding.promotedRiskId}`}
                  className="text-xs text-primary hover:underline"
                  data-testid={`finding-risk-link-${finding.id}`}
                >
                  Ver riesgo generado
                </Link>
              )}
              {canEdit && onPromoteToRisk && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  disabled={promoteDisabled}
                  data-testid={`finding-promote-btn-${finding.id}`}
                  onClick={() => onPromoteToRisk(finding)}
                >
                  {finding.promotedRiskId
                    ? 'Riesgo generado'
                    : promotingId === finding.id
                      ? 'Generando…'
                      : 'Generar riesgo operativo'}
                </button>
              )}
              {finding.qmsRecordId && (
                <Link
                  to={`/registros/no-conformidades/${finding.qmsRecordId}`}
                  className="text-xs text-primary hover:underline"
                  data-testid={`finding-nc-link-${finding.id}`}
                >
                  Ver no conformidad
                </Link>
              )}
              {isNc && canEdit && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  disabled={ncDisabled}
                  data-testid={`finding-nc-btn-${finding.id}`}
                  onClick={() => onEscalate(finding)}
                >
                  {finding.qmsRecordId
                    ? 'NC generada'
                    : escalatingId === finding.id
                      ? 'Generando…'
                      : 'Generar No Conformidad'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
